import React, { useState, useMemo, useCallback } from "react";
import TableCell from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import { ReactComponent as UnsortIcon } from "../../../Assets/rank-tracking-assets/unshort.svg";
import Tooltip from "../../components/tooltip";
import { Checkbox, TablePagination } from "@mui/material";
import { extractDomain, rangeCircleStyle } from "../../../../helperJs/helper";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import KeyboardArrowLeft from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRight from "@mui/icons-material/KeyboardArrowRight";
import { Button } from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import VirtualizeTable from "../DynamicTable/VirtualizeTable";
import { InfoOutlined } from "@mui/icons-material";
import { Link } from "react-router-dom";
import { sites } from "../../../../Navigation/constant";
import BacklinkBuyNowDialog from "./backlinkBuyNowDialog";
import { ZustandStore } from "../../../../store/zustand-store";
import { useTranslation } from "react-i18next";

import Ellipsis from "@semcore/ellipsis";
import Chip from "@mui/material/Chip";
import AddProjectStore from "../../../../store/AddProjectStore";
import { useTableBlur } from "../../custom-hook/useTableBlur";

function TablePaginationActions(
  props,
  anchorItemLen,
  tableMessage,
  handleLoadMoreResultData
) {
  const { page, onPageChange, rowsPerPage } = props;
  const { t } = useTranslation();

  const loadedData = useMemo(() => {
    const status = (page + 1) * rowsPerPage;
    return status === anchorItemLen;
  }, [page, rowsPerPage, anchorItemLen]);

  const handleLoadMoreResultsButtonClick = () => {
    handleLoadMoreResultData();
  };

  const handleBackButtonClick = (event) => {
    onPageChange(event, page - 1);
  };

  const handleNextButtonClick = (event) => {
    onPageChange(event, page + 1);
  };

  return (
    <Box sx={{ flexShrink: 0, ml: 2.5 }}>
      <IconButton
        onClick={handleBackButtonClick}
        aria-label="previous page"
        sx={{
          "& .Mui-disabled": {
            backgroundColor: "transparent !important",
          },
        }}
      >
        <KeyboardArrowLeft />
      </IconButton>
      <IconButton onClick={handleNextButtonClick} aria-label="next page">
        <KeyboardArrowRight />
      </IconButton>

      {tableMessage ? (
        <Button
          className="m-3 mx-4 btn_1"
          disabled={true}
          variant="contained"
          color="primary"
        >
          {tableMessage}
        </Button>
      ) : (
        <Button
          className="m-3 mx-4 btn_1"
          disabled={!loadedData}
          variant="contained"
          color="primary"
          onClick={handleLoadMoreResultsButtonClick}
        >
          {t("backlinks.backlink_analysis.backlinks_table.load_more")}
        </Button>
      )}
    </Box>
  );
}

function isURL(str) {
  var urlPattern = /(\.com|https|www|http)/gi;
  return urlPattern.test(str);
}

const extractURL = (text) => {
  const match = text.match(/https?:\/\/[^\s)"]+/);
  return match ? match[0] : null; // Return the URL or null if not found
};

const BacklinkTable = ({
  data,
  tableData,
  selectAll,
  isCheckboxChecked,
  handleCheckboxSelectAll,
  handleTableSelectedData,
  handleBacklinkSortClick,
  handleTableLoadMore,
  tableMessage,
  tableLoader,
  columns,
  tablePage,
  setTablePage,
}) => {
  const { t } = useTranslation();

  const [openBuyNowDialog, setOpenBuyNowDialog] = useState();
  const anchorItemsPerPage = 100;
  const { get_link_feature } = ZustandStore((state) => state);
  const showNotification = AddProjectStore((state) => state.showNotification);

  sessionStorage.setItem("FREE_TRIAL_TABLE", "backlinkTable");
  const maxAllowedPage = 1; // for trial users
  const shouldTableBlur = useTableBlur({
    currentPage: tablePage,
    blurFromPage: maxAllowedPage,
  });

  function findDomainMatch(url) {
    if (!get_link_feature || get_link_feature?.length === 0) return false;
    // Extract the domain from the given URL
    const targetDomain = extractDomain(url);
    if (targetDomain === "") return false;

    // Check if any object matches the domain
    return (
      get_link_feature?.find(
        (item) => extractDomain(item.URL) === targetDomain
      ) || null
    );
  }

  const Data = useMemo(() => {
    if (data?.length === 0) return [];
    if (shouldTableBlur.showWarning) return data?.slice(0, 1);
    return data;
  }, [data, shouldTableBlur.showWarning]);
  const anchorItemLen = tableData?.length;

  const ranges = [
    { min: -Infinity, max: 30, backgroundColor: "rgb(108, 170, 106)" },
    { min: 30, max: 60, backgroundColor: "rgb(255, 142, 59)" },
    { min: 60, max: Infinity, backgroundColor: "rgb(233, 86, 85)" },
    { default: true, backgroundColor: "rgb(233, 86, 85)" }, // Default color
  ];

  const rowData = useMemo(() => {
    if (Data?.length === 0) return [];
    return Data?.map((item, index) => {
      return {
        id: index,
        domain_authority: (
          <>
            {Math.round(item?.domain_authority / 10) < 5
              ? 0
              : Math.round(item?.domain_authority / 10) || 0}
          </>
        ),
        target_page: (
          <>
            <div className="d-flex flex-column align-items-start">
              {item?.page_from_title && (
                <span className="backlink-table-head font-regular mt-0">
                  <a
                    href={item?.url_from}
                    className="w-100"
                    target="_blank"
                    style={{
                      color: "#000000E5",
                      paddingLeft: "0px",
                    }}
                    rel="noopener noreferrer"
                  >
                    {item?.page_from_title}
                  </a>
                </span>
              )}
              {item?.url_from && (
                <span className="d-flex align-items-center w-100">
                  <a
                    href={item?.url_from}
                    target="_blank"
                    className="d-flex align-items-center"
                    rel="noopener noreferrer"
                    style={{ wordBreak: "break-all" }}
                  >
                    {item?.url_from}{" "}
                  </a>
                </span>
              )}
            </div>
          </>
        ),
        page_authority: (
          <>
            {Math.round(item?.page_authority / 10) < 5
              ? 0
              : Math.round(item?.page_authority / 10) || 0}
          </>
        ),
        backlink_spam_score: (
          <>
            <span
              className="spamscores-table me-1"
              style={rangeCircleStyle(item?.backlink_spam_score, ranges)}
            ></span>
            <span>{`${item?.backlink_spam_score}%`}</span>
          </>
        ),
        anchor: (
          <>
            <div className="d-flex flex-column align-items-start text-start">
              {item?.anchor && (
                <>
                  {isURL(item?.anchor) ? (
                    <span className="d-flex align-items-center w-100">
                      <a
                        href={extractURL(item?.anchor)}
                        target="_blank"
                        className="d-flex align-items-center w-100"
                        rel="noopener noreferrer"
                      >
                        <Ellipsis wMax={180} trim="end">
                          {extractURL(item?.anchor)}
                        </Ellipsis>

                        <OpenInNewIcon
                          sx={{
                            fontSize: "13px",
                            marginLeft: "1px",
                          }}
                        />
                      </a>
                    </span>
                  ) : (
                    <Ellipsis wMax={200} trim="end">
                      {item.anchor}
                    </Ellipsis>
                  )}
                </>
              )}

              <Chip
                label={item.dofollow ? "follow" : "unfollow"}
                size="small"
                sx={{
                  fontSize: "0.9em",
                  color: "#fff",
                  backgroundColor: item.dofollow ? "#7BBA75" : "#F98887",
                }}
              />

              {item?.url_to && (
                <a
                  href={item?.url_to}
                  target="_blank"
                  className="d-flex align-items-center mt-1"
                  rel="noopener noreferrer"
                  style={{ wordBreak: "break-all" }}
                >
                  {item?.url_to}
                </a>
              )}
            </div>
          </>
        ),
        first_seen: (
          <>
            {item?.first_seen.split(" ", 1)}
            <br />
            {item?.last_seen.split(" ", 1)}
          </>
        ),
        url_from: item?.url_from,
        domain_from: item?.domain_from,
        url_feature: findDomainMatch(item?.url_from),
      };
    });
  }, [Data, get_link_feature]);

  const handleChangePage = (event, newPage) => {
    const isRestricted =
      shouldTableBlur.showWarning && newPage >= maxAllowedPage;
    if (isRestricted) {
      // Optional: show toast or popup
      showNotification({
        message:
          "🚫You are on free trial now, Please pay and unlock more pages",
        severity: "error",
      });
      return;
    }
    const maxPage = Math.max(
      0,
      Math.ceil(tableData?.length / anchorItemsPerPage) - 1
    );
    selectAll && handleCheckboxSelectAll();
    if (newPage < 0) {
      setTablePage(0); // Reset to the first page if negative
    } else if (newPage > maxPage) {
      setTablePage(maxPage); // Reset to the last page if exceeding limits
    } else {
      setTablePage(newPage); // Otherwise, set the provided page
    }
  };

  function fixedHeaderContent() {
    return (
      <TableRow>
        <TableCell
          padding="checkbox"
          key={"checkbox"}
          variant="head"
          align="left"
          onClick={handleCheckboxSelectAll}
          sx={{
            backgroundColor: "#F4F5F9",
            fontSize: "0.9em",
            color: "#191B23",
            padding: "5px 8px",
            maxWidth: "30px"
          }}
        >
          <Checkbox
            className="p-1"
            sx={{
              color: "#C4C7CF",
              "&.Mui-checked": {
                color: "#365486",
              },
            }}
            checked={selectAll}
            readOnly
          />
        </TableCell>

        {columns.map((column) => (
          <TableCell
            key={column.dataKey}
            variant="head"
            align={column.align}
            className="font-medium"
            sx={{
              fontSize: "0.9em",
              color: "#191B23",
              padding: "5px",
              backgroundColor: "#F4F5F9",
              width: column?.width ? column.width : "100%",
            }}
          >
            <span
              className={column?.sort && column?.dataKey ? "point" : ""}
              onClick={() => {
                column?.sort &&
                  column?.dataKey &&
                  handleBacklinkSortClick(column.dataKey);
              }}
            >
              {column.label}
              {column?.sort && column?.dataKey && (
                <span className="ms-2">
                  <UnsortIcon />
                </span>
              )}
              {column?.tooltip && <Tooltip title={column.tooltip} />}
            </span>
          </TableCell>
        ))}
      </TableRow>
    );
  }

  function rowContent(_index, row) {
    return (
      <React.Fragment>
        <TableCell
          padding="checkbox"
          key={"checkbox" + row.id}
          sx={{
            fontSize: "1em",
            color: "#191B23",
            padding: "5px 8px",
          }}
          onClick={() => handleTableSelectedData(Data?.[row.id], row.id)}
        >
          <Checkbox
            className="p-1"
            sx={{
              color: "#C4C7CF",
              "&.Mui-checked": {
                color: "#365486",
              },
            }}
            checked={isCheckboxChecked(row.id)}
          />
        </TableCell>
        {columns.map((column) => {
          if (column.dataKey === "action") {
            return (
              <TableCell
                key={column.dataKey}
                align={column.align}
                sx={{
                  fontSize: "1em",
                  color: "#191B23",
                  padding: "5px",
                }}
              >
                <div className="d-flex align-items-center justify-content-start gap-2">
                  <Button
                    style={{
                      backgroundColor: "#6caa6a",
                      color: "#ffff",
                      textWrap: "nowrap",
                    }}
                    className="transform-off"
                    title="Buy Now"
                    disabled={!Boolean(row?.url_feature)}
                    variant="contained"
                    onClick={() => handleClickBuyNowDialog(row?.url_feature)}
                  >
                    {t(
                      "backlinks.backlink_analysis.backlinks_table.interested"
                    )}
                  </Button>

                  <Link
                    to={`${sites}?domain=${row?.domain_from}`}
                    target="_blank"
                    className=""
                  >
                    <Button
                      className="icon-btn transform-off"
                      title="View Information"
                      variant="contained"
                      startIcon={<InfoOutlined />}
                    >
                      {t("backlinks.backlink_analysis.backlinks_table.info")}
                    </Button>
                  </Link>
                </div>
              </TableCell>
            );
          } else {
            return (
              <TableCell
                key={column.dataKey}
                align={column.align}
                sx={{
                  fontSize: "1em",
                  color: "#191B23",
                  padding: "5px",
                }}
                className="font-medium"
              >
                {row[column.dataKey]}
              </TableCell>
            );
          }
        })}
      </React.Fragment>
    );
  }

  const handleLoadMoreResultData = () => {
    if (Data?.length === 0) return;
    const isRestricted = shouldTableBlur.isTrialUser;
    if (isRestricted) {
      showNotification({
        message:
          "🚫You are on free trial now, Please pay and unlock more pages",
        severity: "error",
      });
      return;
    }
    handleTableLoadMore();
  };

  const handleClickBuyNowDialog = (item) => {
    setOpenBuyNowDialog(item);
  };

  const handleCloseBuyNowDialog = useCallback(() => setOpenBuyNowDialog(), []);

  return (
    <>
      <Paper
        className={"w-100 mt-3 vir-table-border"}
        sx={{
          backgroundColor: "background.paper",
          boxShadow: "none",
          "& .MuiTableContainer-root": {
            boxShadow: "none",
            borderRadius: 0,
          },
        }}
      >
        <VirtualizeTable
          {...{
            columns,
            rowContent,
            data: rowData,
            loading: tableLoader,
            fixedHeaderContent,
            columnLength: columns?.length + 1,
            isTableBlur: shouldTableBlur.showWarning,
          }}
        />

        <TablePagination
          sx={{
            borderTop: "1px solid rgba(224, 224, 224, 1)",
            backgroundColor: "background.paper",
            cursor: "default",
            "& .MuiTablePagination-actions": {
              width: 90,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            },
            "& .MuiTablePagination-displayedRows": {
              padding: 0,
              margin: 0,
            },
            "& .MuiTablePagination-selectLabel": {
              padding: 0,
              margin: 0,
            },
          }}
          component="div"
          disabled={tableData?.length === 0}
          count={tableData?.length}
          page={tablePage}
          rowsPerPageOptions={[100]}
          rowsPerPage={100}
          onPageChange={handleChangePage}
          onRowsPerPageChange={() => {}}
          ActionsComponent={(props) =>
            TablePaginationActions(
              props,
              anchorItemLen,
              tableMessage,
              handleLoadMoreResultData
            )
          }
        />
        {Boolean(openBuyNowDialog) && (
          <BacklinkBuyNowDialog
            open={openBuyNowDialog}
            onClose={handleCloseBuyNowDialog}
          />
        )}
      </Paper>
    </>
  );
};

export default BacklinkTable;
